/**
 * Base API Client
 * Modern fetch-based API client with TypeScript support
 */

import { handleUnauthorized } from '@agion/auth'
import { config } from '../utils/config'
import { refusalText } from '@agion/ui/api-failure'
import { ChatApiError, isChatApiError } from './api-failure'

/**
 * The text of a failed response (agion/Agion#2327). A server words a refusal
 * in `message` (a sentence), in `error` (the reason itself when it is the only
 * field, as orchestration-service writeError sends it, or a code beside the
 * sentence in `message`) or in `detail`. Each counts only as a non-empty
 * string. Without one, the status, with its status text when there is one:
 * HTTP/2 has none.
 */
function failureText(body: unknown, status: number, statusText: string): string {
  return refusalText(body) ?? (statusText ? `HTTP ${status}: ${statusText}` : `HTTP ${status}`)
}

/** The sentence of a refusal body (shared with admin-ui: @agion/ui/api-failure). */
export { refusalText }

/**
 * The sentence of a refused response, for a caller that fetches without
 * ApiClient. It reads the body once: a JSON body through refusalText, a
 * plain-text body (Go http.Error) as its trimmed text. An empty body, an HTML
 * page (a proxy's error page) or an unreadable body gives undefined, so the
 * caller shows its own text.
 */
export async function readRefusal(response: Response): Promise<string | undefined> {
  const raw = (await response.text().catch(() => '')).trim()
  if (raw === '' || raw.startsWith('<')) return undefined
  try {
    return refusalText(JSON.parse(raw))
  } catch {
    return raw
  }
}

// Cancellable request interface
export interface CancellableRequest<T = unknown> {
  abort: () => void
  promise: Promise<T>
}

export class ApiClient {
  private baseURL: string
  private defaultHeaders: Record<string, string>
  private pendingRequests: Map<string, AbortController>

  constructor(baseURL?: string) {
    // Use config module instead of direct import.meta.env access
    this.baseURL = baseURL || config.apiUrl
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    }
    this.pendingRequests = new Map()
  }

  /**
   * Generate a unique request ID
   */
  private generateRequestId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Get the number of pending requests
   */
  get pendingRequestCount(): number {
    return this.pendingRequests.size
  }

  /**
   * Cancel a specific request by ID
   */
  cancelRequest(requestId: string): void {
    const controller = this.pendingRequests.get(requestId)
    if (controller) {
      controller.abort()
      this.pendingRequests.delete(requestId)
      console.log(`[API] Cancelled request: ${requestId}`)
    }
  }

  /**
   * Cancel all pending requests
   */
  cancelAllRequests(): void {
    console.log(`[API] Cancelling ${this.pendingRequests.size} pending requests`)
    this.pendingRequests.forEach((controller, _requestId) => {
      controller.abort()
    })
    this.pendingRequests.clear()
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    cancellable: boolean = false,
    requestId?: string
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`

    // Create AbortController if cancellable
    let controller: AbortController | undefined
    if (cancellable && requestId) {
      controller = new AbortController()
      this.pendingRequests.set(requestId, controller)
    }

    // Development logging - Log all API requests
    if (process.env.NODE_ENV === 'development') {
      console.log('[API Request]', {
        url,
        method: options.method || 'GET',
        hasCredentials: true, // credentials: 'include' is always set
        endpoint,
        cancellable,
        requestId,
        timestamp: new Date().toISOString()
      })
    }

    // Don't include default Content-Type header for FormData requests
    const headers = options.body instanceof FormData
      ? { ...options.headers }  // Don't include default headers for FormData
      : { ...this.defaultHeaders, ...options.headers }

    // BFF Pattern: No Authorization header needed
    // Authentication is handled by APISIX forward-auth plugin which:
    // 1. Reads the agion_sso_session cookie
    // 2. Calls auth-service /session/resolve to get the JWT
    // 3. Injects the Authorization header into upstream requests
    // The client just needs to send cookies with credentials: 'include'

    const requestConfig: RequestInit = {
      ...options,
      headers,
      // BFF Pattern: Session cookie authentication
      // The agion_sso_session HttpOnly cookie contains an opaque session ID
      // APISIX forward-auth resolves this to a JWT for upstream services
      credentials: 'include',
      signal: controller?.signal, // Add abort signal if available
    }

    try {
      const response = await fetch(url, requestConfig)

      // Clean up the pending request on successful response
      if (cancellable && requestId) {
        this.pendingRequests.delete(requestId)
      }

      if (!response.ok) {
        // Handle 401 Unauthorized - soft revalidation via @agion/auth.
        // handleUnauthorized de-dupes concurrent calls and only redirects
        // on confirmed-gone — a single transient 401 will NOT bounce the
        // user out of an active workflow.
        if (response.status === 401) {
          console.warn('[API] 401 received, probing session via @agion/auth')
          void handleUnauthorized()
          throw new ChatApiError('Unauthorized', { kind: 'unauthorized', status: 401 })
        }

        const errorData: unknown = await response.json().catch(() => ({}))
        // The body stays on the error, as `data` and as `response.data`, for a
        // caller that reads the refusal's code (the launch's missing start
        // values, agion/Agion#2413).
        throw new ChatApiError(failureText(errorData, response.status, response.statusText), {
          kind: 'http',
          status: response.status,
          data: errorData,
        })
      }

      // Handle empty responses
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return {} as T
      }

      return await response.json()
    } catch (error) {
      // Clean up pending request on error
      if (cancellable && requestId) {
        this.pendingRequests.delete(requestId)
      }

      // Handle AbortError specifically
      if (isChatApiError(error)) throw error

      if (error instanceof Error && error.name === 'AbortError') {
        console.log('[API] Request cancelled:', requestId)
        throw new ChatApiError('Request cancelled', { kind: 'cancelled' })
      }

      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ChatApiError('Network error: Unable to connect to the server', { kind: 'network' })
      }
      // Safari words the same refusal "Load failed"; fetch rejects only with a
      // TypeError when no response arrives. The text stays as the browser sent it.
      if (error instanceof TypeError) {
        throw new ChatApiError(error.message, { kind: 'network' })
      }
      throw error
    }
  }

  async get<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
    // Handle both absolute and relative URLs
    let finalUrl = endpoint

    if (params) {
      const urlParams = new URLSearchParams(params)
      finalUrl = `${endpoint}${endpoint.includes('?') ? '&' : '?'}${urlParams.toString()}`
    }

    return this.request<T>(finalUrl)
  }

  /**
   * GET request with cancellation support
   */
  getCancellable<T>(endpoint: string, params?: Record<string, string>): CancellableRequest<T> {
    const requestId = this.generateRequestId()

    // Handle both absolute and relative URLs
    let finalUrl = endpoint

    if (params) {
      const urlParams = new URLSearchParams(params)
      finalUrl = `${endpoint}${endpoint.includes('?') ? '&' : '?'}${urlParams.toString()}`
    }

    const promise = this.request<T>(finalUrl, {}, true, requestId)

    return {
      promise,
      abort: () => this.cancelRequest(requestId)
    }
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  /**
   * POST request with cancellation support
   */
  postCancellable<T>(endpoint: string, data?: unknown): CancellableRequest<T> {
    const requestId = this.generateRequestId()

    const promise = this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }, true, requestId)

    return {
      promise,
      abort: () => this.cancelRequest(requestId)
    }
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    })
  }

  async uploadFile<T>(endpoint: string, file: File, additionalData?: Record<string, unknown>): Promise<T> {
    const formData = new FormData()
    formData.append('file', file)

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, JSON.stringify(value))
      })
    }

    return this.request<T>(endpoint, {
      method: 'POST',
      body: formData,
      headers: {
        // Don't set Content-Type for FormData - browser will set it with boundary
      },
    })
  }

  async uploadFiles<T>(endpoint: string, files: File[], additionalData?: Record<string, unknown>): Promise<T> {
    const formData = new FormData()

    files.forEach(file => {
      formData.append('files', file)
    })

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        // Handle different data types properly for form fields
        if (typeof value === 'boolean') {
          formData.append(key, value.toString())
        } else if (typeof value === 'string' || typeof value === 'number') {
          formData.append(key, value.toString())
        } else {
          formData.append(key, JSON.stringify(value))
        }
      })
    }

    return this.request<T>(endpoint, {
      method: 'POST',
      body: formData,
      headers: {
        // Don't set Content-Type for FormData
      },
    })
  }

  setBaseURL(baseURL: string) {
    this.baseURL = baseURL
  }

  // BFF Pattern: No setAuthToken or clearAuth methods needed
  // Authentication is handled entirely via HttpOnly session cookies
  // managed by the backend (auth-service) and APISIX forward-auth
}

export const apiClient = new ApiClient()

// Export types for API responses
export interface ApiError {
  detail: string
  type?: string
  code?: string
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  size: number
  pages: number
}
